import { map } from './layers-init.js'
import { baronTiles}  from './productMapping.js'
import { iconLightning } from './icons-init.js'
import { getSignature, getProductInstances, getLightning, noDataHandler } from './getData.js'

/* INITIALIZE WEATHER LAYERS */

let weatherTiles
let lightning = new L.layerGroup()

let weatherTilesGroup = new L.layerGroup()
let activeLayers = []

/* BARON SIGNATURE 
  Baron requires a signature (essentially a timestamp) be appended at the end of the tile request URL.
  The signature needs to be updated every 10 minutes.
  The signature will always be stored in the "signature" variable initialized below.
*/
let signature

/*  Compare the load time of the page (defined as "start_time") with the current time (defined as "current_time").
    This comparison happens every 10,000 ms (every 10 seconds).
    If it has been more than 10 minutes (600 seconds), the signature is no longer valid.
*/ 
let start_time = Math.floor((new Date()).getTime() / 1000)
const signature_interval = setInterval(function() {
  const current_time = Math.floor((new Date()).getTime() / 1000)
  // If has been more than 600 seconds, we need to get a new signature from our server
  if (current_time - start_time > parseInt(600)) {
    getSignature().then(updatedSignature => { 
      signature = updatedSignature
      start_time = Math.floor((new Date()).getTime() / 1000)
    })
  }
}, 10000)


/*  initWeatherLayer is called upon loading a weather tile layer.
    It retrieves the signature from the server, called "initSignature",
    and passes it to getInstances (which retrieves the valid times for the desired weather product)
*/
const initWeatherLayer = () => {
  getSignature().then(initSignature => {
    signature = initSignature
  })
  .catch(error => console.log(error, 'Error with initWeatherLayer'))
}

/*  getInstances gets the valid times (called an "instance") for the requested weather product.
    For example, the user requests radar tiles. The radar tiles are produced at certain times.
    Once the instance(s) are found, pass those to the "weatherLayer" function for plotting on the map
*/
const getInstances = (product = baronTiles.radar.composite_250m) => {
  return new Promise((resolve, reject) => {
    getProductInstances(product)
      .then(response => {
        let instances = JSON.parse(response)
      // If there are no instances, call the noDataHandler (which displays message to the user)
        if (!instances) {
          noDataHandler(product)
          return
        }
        resolve(instances)
        reject('Instance of product was not found, or other error occurred')
      })
      .catch(error => console.log(error, 'Error with getInstances'))
  })
}

/*  weatherLayer adds the TMS tile layer on the map
    Refer to: https://leafletjs.com/reference-1.5.0.html#tilelayer for more details on the options
*/
const weatherLayer = (instance, pane) => {
  return new Promise((resolve, reject) => {
    weatherTiles = L.tileLayer(`${instance}{z}/{x}/{y}.png?{sig}`, {
      tms: true,
      opacity: 0.7,
      pane,
      className: 'weather-tile-layer',
      subdomains: ["", "1", "2", "3", "4", "5"],
      sig() {
        return signature
      }
    })
    resolve(weatherTiles)
  })
}

const generateTileLayers = (products) => {
  weatherTilesGroup = new L.layerGroup()

  products.map(product => {
    /*  Create a pane that has a name of the product code
        Map panes are used to change the z-index (display order) of layers on the map
        For example, we often want to display warnings above the radar layer
        Refer to: https://leafletjs.com/examples/map-panes/ for more details on map panes
    */ 
    map.createPane(product.code)
    map.getPane(product.code).style.zIndex = product.zIndex

    /*  Add layers to "activeLayers" array for tracking and layer management
        Each layer has metadata, like refresh intervals, for weather data refreshing
        that we will need later. 
        Refer to "data-manage.js" to see how the data are refreshed.
    */
    activeLayers.push(product)

    /*  Retrieve the instance (i.e. timestamp) of the product passed in
        Pass the instance and the product.code (which is the name of the map pane)
        to the weatherLayer function, which will plot the tiles appropriately
    */
    getInstances(product.code)
      .then(instances => weatherLayer(instances[0], product.code))
      .then(weatherTiles => weatherTilesGroup.addLayer(weatherTiles))
      .then(_ => weatherTilesGroup.addTo(map))
      .catch(error => console.log(error, 'Error when plotting tile layers'))
  })
}

/*  layerLightning retrieves point lightning strike data from the server
    Each strike is added to a layer group called "lightning" (which was initialized earlier)
    Refer to: https://leafletjs.com/examples/layers-control/ for more details on Layer Groups
*/
const layerLightning = () => {
  // Get the lightning data from the server
  getLightning()
    .then(data => { 
      data.map(strike => {
        const lat = strike[1]
        const lon = strike[0]

        const marker = new L.marker([lat, lon], {icon: iconLightning})
        lightning.addLayer(marker)
      })
    })
    .catch(error => console.log(error, 'Error when plotting lightning strikes'))

  map.addLayer(lightning)
}

/* Clear active layers */
const clearActiveLayers = () => activeLayers = []

export { initWeatherLayer, generateTileLayers, lightning, layerLightning, weatherTilesGroup, activeLayers, clearActiveLayers }